import AccordionV2 from "@Components/Accordian";
import Button from "@Components/Button";
import { useRef, useState } from "react";
import styles from "./faq.module.scss";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import SearchInput from "./components/SearchInput";
import AmenitiesList from "./components/AmenitiesList";
import debounce from "helpers/debounce";
import LeadFormDispatcher from "@Components/LeadFormModal/actions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import DetailsSummaryAccordionItem from "./components/DetailsSummaryAccordionItem/DetailsSummaryAccordionItem";
import OfferChevronDown from "@Icons/OfferChevronDown";
import replaceAmberDomainInLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/replaceAmberDomainInLinks";
import validateHtmlForSSR from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/validateHtmlForSSR";

const seoMicroDataAttributes = {
  faqpage: { itemscope: "", itemtype: "https://schema.org/FAQPage" },
  question: { itemscope: "", itemprop: "mainEntity", itemtype: "https://schema.org/Question" },
  name: { itemprop: "name" },
  answer: { itemscope: "", itemprop: "acceptedAnswer", itemtype: "https://schema.org/Answer" },
  text: { itemprop: "text" },
};

type FAQsTypes = {
  isSeoTestPage?: boolean;
  data: Array<any>;
  propertyPageGtagEvents?: boolean;
  isDesktop?: boolean;
  dataTestId?: string;
  dataAmenities?: Array<any>;
  addSearchBar?: boolean;
  defaultFirstOpen?: boolean;
};

const FAQs = ({
  isSeoTestPage = false,
  data,
  propertyPageGtagEvents = false,
  isDesktop = false,
  dataTestId,
  dataAmenities = [],
  addSearchBar = false,
  defaultFirstOpen = false,
}: FAQsTypes) => {
  const accordionRef = useRef<HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [faqData, setFaqData] = useState(data || []);
  const [amenitiesData, setAmenitiesData] = useState<any[]>([]);
  const { t } = useTranslationHook();

  const INITIAL_FAQ_COUNT = 5;
  const shouldShowShowMore = faqData.length > INITIAL_FAQ_COUNT;

  const showMoreText = t("propertypage.sectionregioninfo.faqs.viewallquestions");
  const showLessText = t("propertypage.sectionregioninfo.faqs.viewlessquestions");

  const faqSearchEventTrack = debounce((val: any) => {
    gtagEventEmitter({
      action: "faq_search",
      category: "PropertyPage_Faq",
      value: val,
    });
  }, 1000);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value.toLowerCase();
    setSearchTerm(e.target.value);
    faqSearchEventTrack(newSearchTerm);

    if (newSearchTerm === "") {
      setFaqData(data);
      setAmenitiesData([]);
      return;
    }
    const highlightSearchTerm = (question: string, searchTermLocal: string) => {
      const regex = new RegExp(`(${searchTermLocal})`, "gi");
      return question.replace(regex, "<mark style='background-color: #00FFA9;'>$1</mark>");
    };
    const filteredFaqData = data
      .filter((item) => item.question.toLowerCase().includes(newSearchTerm))
      .map((item) => ({
        ...item,
        question: highlightSearchTerm(item.question, newSearchTerm),
      }));

    setFaqData(filteredFaqData);

    const filteredAmenitiesData = dataAmenities
      .filter((item) => item.name && item.name?.toLowerCase().includes(newSearchTerm))
      .map((item) => ({
        ...item,
        highlightText: highlightSearchTerm(item.name, newSearchTerm),
      }));
    setAmenitiesData(filteredAmenitiesData);
  };
  const handleClearSearch = () => {
    setSearchTerm("");
    setFaqData(data);
    setAmenitiesData([]);
  };
  const toggleShowMore = () => {
    if (propertyPageGtagEvents) {
      gtagEventEmitter({
        action: "property_FAQ_loaded",
        category: PAGE.INVENTORY,
        label: "",
      });
    }
  };

  const renderQA = ({
    question,
    answer,
    dataTestId: testId,
  }: {
    question: string;
    answer: string;
    dataTestId?: string;
  }) => {
    // Validate and sanitize HTML for SSR
    const validatedQuestion = validateHtmlForSSR(replaceAmberDomainInLinks(question));
    const validatedAnswer = validateHtmlForSSR(replaceAmberDomainInLinks(answer));

    return {
      title: (
        <h3
          className={styles.title}
          {...(isSeoTestPage && seoMicroDataAttributes["name"])}
          data-testid={`searchPage-${dataTestId}-question`}
        >
          {validatedQuestion ? (
            <div dangerouslySetInnerHTML={{ __html: validatedQuestion }} />
          ) : null}
        </h3>
      ),
      description: (
        <div
          data-testid={`${testId}-answer`}
          className={styles.typography}
          {...(isSeoTestPage && seoMicroDataAttributes["answer"])}
        >
          {validatedAnswer ? (
            <div
              dangerouslySetInnerHTML={{ __html: validatedAnswer }}
              {...(isSeoTestPage && seoMicroDataAttributes["text"])}
              data-testid={`searchPage-${dataTestId}-answer`}
            />
          ) : null}
        </div>
      ),
    };
  };

  const renderAccordion = (
    start: number,
    end: number,
    showHeading: boolean,
    additionalProps = {},
  ) => (
    <>
      {showHeading && searchTerm && (
        <div className={styles.faqHeading}>{`${t(
          "propertypage.sectionregioninfo.faqs.frequentlyaskedquestions",
        )} (${faqData.length})`}</div>
      )}
      <AccordionV2
        data={faqData
          .slice(start, end)
          .map(({ question, answer }) => renderQA({ question, answer, dataTestId }))}
        {...(isSeoTestPage && seoMicroDataAttributes["faqpage"])}
        className={styles.item}
        isDesktop={isDesktop}
        CustomAccordionItem={DetailsSummaryAccordionItem}
        defaultFirstOpen={defaultFirstOpen}
        {...additionalProps}
      />
    </>
  );

  const renderAllItems = () => {
    const createUniqueId = () => {
      if (!faqData.length) return "faq-empty";
      const firstQuestion = faqData[0]?.question || "";
      const simpleHash = firstQuestion.substring(0, 10).replace(/[^a-zA-Z0-9]/g, "");
      return `faq-${simpleHash}-${faqData.length}`;
    };

    const uniqueId = createUniqueId();

    return (
      <>
        {amenitiesData.length > 0 && (
          <>
            <AmenitiesList
              amenitiesData={amenitiesData}
              dataTestId={dataTestId}
              isDesktop={isDesktop}
            />
            {faqData.length > 0 && (
              <div className={styles.dividerContainer}>
                <div className={styles.divider} />
              </div>
            )}
          </>
        )}
        {faqData.length > 0 && renderAccordion(0, INITIAL_FAQ_COUNT, true)}

        {/* using checkbox + label pattern for ssr clicks */}
        {shouldShowShowMore && (
          <div className={styles.showMoreLessContainer}>
            <input
              type="checkbox"
              id={uniqueId}
              className={styles.showMoreCheckbox}
              data-testid={`${dataTestId}-showMore-checkbox`}
            />

            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
            <label
              htmlFor={uniqueId} // Uses unique ID
              className={styles.showMoreLabel}
              data-testid={`${dataTestId}-showMore-label`}
              onClick={toggleShowMore}
            >
              <span className={styles.showMoreText}>
                {showMoreText} ({faqData.length - INITIAL_FAQ_COUNT})
                <OfferChevronDown height="18" width="18" />
              </span>
            </label>

            <div className={styles.additionalFAQs}>
              {renderAccordion(INITIAL_FAQ_COUNT, faqData.length, false, {
                className: styles.item,
                addDivider: true,
                defaultFirstOpen: false,
              })}

              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
              <label
                htmlFor={uniqueId} // Uses the same unique ID
                className={styles.showLessLabel}
                data-testid={`${dataTestId}-showLess-label`}
                onClick={toggleShowMore}
              >
                <span className={styles.showLessText}>
                  {showLessText}
                  <span className={styles.rotated}>
                    <OfferChevronDown height="18" width="18" />
                  </span>
                </span>
              </label>
            </div>
          </div>
        )}
      </>
    );
  };
  const renderFallBack = () => (
    <>
      <div className={styles.fallbackText}>
        <div data-testid="FAQ-No-Match-Message" className={styles.upperHeading}>
          {t("propertypage.sectionregioninfo.faqs.nomatchesmessage")}
        </div>
        <div data-testid="FAQ-Experts-Message" className={styles.subHeading}>
          {t("propertypage.sectionregioninfo.faqs.expertsmessage")}
        </div>
      </div>
    </>
  );

  const { data: dataLeadForm } = useSelector(
    ({ propertyPageData }: AppState) => ({
      data: propertyPageData.data,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const openLeadFormHandler = async () => {
    gtagEventEmitter({
      action: "ask_question_faq_cta_tapped",
      category: "PropertyPage",
    });
    const leadFormDispatcher = new LeadFormDispatcher(dispatch);
    leadFormDispatcher.handleOnEnquireClick({
      data: dataLeadForm,
      source: "ask_question_faq_cta",
      page: 1,
      heading: "Ask Question",
    });
  };

  return (
    <section ref={accordionRef}>
      {addSearchBar ? (
        <SearchInput
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
        />
      ) : null}
      {faqData.length > 0 || amenitiesData.length > 0 ? renderAllItems() : renderFallBack()}

      {searchTerm && (
        <div className={styles.askQuestionContainer}>
          <div className={styles.text}>
            {t("propertypage.sectionregioninfo.faqs.noanswermessage")}
          </div>
          <Button
            dataTestId="Ask-Question-Button"
            variant="outline"
            onClick={openLeadFormHandler}
            className={styles.askButton}
          >
            {t("propertypage.sectionregioninfo.faqs.askquestion")}
          </Button>
        </div>
      )}
    </section>
  );
};

export default wrapperHOC(FAQs, {
  componentName: "FAQs-SearchPage",
  showForChina: true,
});
